import React, { useCallback, useEffect, useRef } from "react";
import { Box } from "@mui/material";
import { LoadScript } from "@react-google-maps/api";
import { googleApiKey } from "utils/config";
import { getStatesGeometry } from "./utils";
import { STATE_FULL_NAME } from "utils/constants";

const libraries = ["places", "drawing", "geometry"];

const MapComponent = ({ locationType, location }) => {
  const map = useRef(null);
  const mapLayers = useRef(null);
  const markers = useRef([]);
  const circles = useRef([]);
  const infoWindows = useRef([]);
  const mapContainerRef = useRef(null);


  const initMap = useCallback(() => {
    if (!window.google) return;

    map.current = new window.google.maps.Map(mapContainerRef.current, {
      center: { lat: 39.661, lng: -98.048 },
      zoom: 4,
      zoomControl: true,
      mapTypeControl: false,
      scaleControl: false,
      streetViewControl: false,
      rotateControl: false,
      fullscreenControl: false,
      disableDefaultUi: false,
    });
  }, []);

  useEffect(() => {
    if (window.google) {
      initMap();
    }
  }, [initMap]);

  useEffect(() => {
    if (!map.current || !location) return;

    switch (locationType) {
      case "state":
        resetMap();
        location.states &&
          !!location.states.length &&
          getStatesGeometry(location.states).then((res) =>
            draw(location.states.map((l) => STATE_FULL_NAME[l]), res)
          );
        break;
      case "local":
        resetMap();
        for (let i = 0; i < location.length; i++) {
          drawMarkerWithLatLng(
            parseFloat(location[i].lat),
            parseFloat(location[i].lng),
            location[i].radius
          );
        }
        break;
      default:
        resetMap();
    }
  }, [location, locationType]);

const resetMap = () => {
  markers.current.forEach((m) => m.setMap(null));
  markers.current = [];

  circles.current.forEach((c) => c.setMap(null));
  circles.current = [];

  infoWindows.current.forEach((i) => i.close());
  infoWindows.current = [];

  if (mapLayers.current) {
    mapLayers.current.forEach((layer) => {
      map.current?.data?.remove(layer);
    });
    mapLayers.current = null;
  }
};

  const draw = (locations, geoJson) => {
    if (!map.current) return;

    map.current.data.setStyle({
      fillColor: "#FF5252",
      strokeWeight: 1,
      fillOpacity: 0.5,
      strokeColor: "#FF0000",
    });

    mapLayers.current = map.current.data.addGeoJson(geoJson);
    const bounds = new window.google.maps.LatLngBounds();
    const geocoder = new window.google.maps.Geocoder();

    locations.forEach((location) => {
      geocoder.geocode({ address: location, region: "US" }, (results, status) => {
        if (status === window.google.maps.GeocoderStatus.OK && results?.length) {
          const viewport = results[0].geometry.viewport;
          bounds.union(viewport);
          map.current.fitBounds(bounds, 20);
        }
      });
    });
  };

const drawMarkerWithLatLng = (lat, lng, radius) => {
  if (!map.current) return;

  const position = { lat, lng };

  const marker = new window.google.maps.Marker({
    position,
    map: map.current,
    icon: {
      url: "http://maps.google.com/mapfiles/ms/icons/red-dot.png",
      scaledSize: new window.google.maps.Size(32, 32),
    },
  });
  markers.current.push(marker); // Store marker

  const circle = new window.google.maps.Circle({
    strokeColor: "#FF0000",
    strokeOpacity: 0.8,
    strokeWeight: 2,
    fillColor: "#FF0000",
    fillOpacity: 0.35,
    center: position,
    radius: radius * 1609.34,
    map: map.current,
  });
  circles.current.push(circle); // Store circle

  map.current.fitBounds(circle.getBounds(), 20);
};

  return (
    <Box
      ref={mapContainerRef}
      sx={{
        height: 320,
        mt: 2.5, // 20px equivalent in MUI spacing
        width: "100%",
      }}
    />
  );
};

const Map = (props) => (
  <LoadScript
    googleMapsApiKey={googleApiKey}
    libraries={libraries}
    loadingElement={<Box sx={{ height: 320 }} />}
  >
    <MapComponent {...props} />
  </LoadScript>
);

export default Map;
